import { FormEvent, useCallback, useEffect, useState } from "react";
import {
    blockDeletedNominationEntry,
    deleteDeletedNominationArchiveEntry,
    fetchDeletedNominationEntries,
    restoreDeletedNominationEntry,
    type DeletedNominationEntry,
} from "../api/tcg";

type AdminDeletedNominationsProps = {
    token: string;
    categoryId: number;
    reloadKey: number;
    onChanged?: () => Promise<void> | void;
};

function formatDateTime(value?: string | null): string {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("de-DE");
}

function deletedActionLabel(action: DeletedNominationEntry["deleted_action"]): string {
    return action === "delete_and_block" ? "Gelöscht & blockiert" : "Nur gelöscht";
}

function getValidArchiveId(entry: DeletedNominationEntry): number | null {
    const archiveId = Number(entry.id);
    return Number.isInteger(archiveId) && archiveId > 0 ? archiveId : null;
}

function AdminDeletedNominations({
    token,
    categoryId,
    reloadKey,
    onChanged,
}: AdminDeletedNominationsProps) {
    const [entries, setEntries] = useState<DeletedNominationEntry[]>([]);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const loadDeletedEntries = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const nextEntries = await fetchDeletedNominationEntries(token, categoryId, search);
            setEntries(nextEntries);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Gelöschte Nominierungen konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    }, [token, categoryId, search]);

    useEffect(() => {
        void loadDeletedEntries();
    }, [loadDeletedEntries, reloadKey]);

    function handleSearch(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setMessage(null);
        setError(null);
        const nextSearch = searchInput.trim();
        if (nextSearch === search) {
            void loadDeletedEntries();
        } else {
            setSearch(nextSearch);
        }
    }

    function resetSearch() {
        setSearchInput("");
        setMessage(null);
        setError(null);

        if (search === "") {
            void loadDeletedEntries();
        } else {
            setSearch("");
        }
    }

    async function reloadAfterAction() {
        await loadDeletedEntries();
        await onChanged?.();
    }

    async function handleRestore(entry: DeletedNominationEntry) {
        if (busyId !== null) return;

        const archiveId = getValidArchiveId(entry);
        if (archiveId === null) {
            setMessage(null);
            setError("Archiv-ID ist ungültig.");
            return;
        }

        const confirmed = window.confirm(
            "Möchtest du diesen gelöschten Eintrag wirklich wiederherstellen? " +
            "Er erscheint danach wieder in den Nominierungsständen. Falls der Begriff zusätzlich blockiert ist, " +
            "bleiben neue Nominierungen weiterhin blockiert, bis die Blocklist angepasst wird."
        );
        if (!confirmed) return;

        setBusyId(archiveId);
        setError(null);
        setMessage(null);
        try {
            await restoreDeletedNominationEntry(token, archiveId);
            await reloadAfterAction();
            setMessage("Gelöschter Eintrag wurde wiederhergestellt.");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Gelöschter Eintrag konnte nicht wiederhergestellt werden.");
        } finally {
            setBusyId(null);
        }
    }

    async function handleBlock(entry: DeletedNominationEntry) {
        if (busyId !== null) return;

        const archiveId = getValidArchiveId(entry);
        if (archiveId === null) {
            setMessage(null);
            setError("Archiv-ID ist ungültig.");
            return;
        }

        const confirmed = window.confirm(
            "Möchtest du diesen gelöschten Eintrag nachträglich blockieren? " +
            "Danach können Nutzer diesen Begriff nicht erneut nominieren."
        );
        if (!confirmed) return;

        setBusyId(archiveId);
        setError(null);
        setMessage(null);
        try {
            await blockDeletedNominationEntry(token, archiveId);
            await reloadAfterAction();
            setMessage("Gelöschter Eintrag wurde nachträglich blockiert.");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Gelöschter Eintrag konnte nicht nachträglich blockiert werden.");
        } finally {
            setBusyId(null);
        }
    }

    async function handleArchiveDelete(entry: DeletedNominationEntry) {
        if (busyId !== null) return;

        const archiveId = getValidArchiveId(entry);
        if (archiveId === null) {
            setMessage(null);
            setError("Archiv-ID ist ungültig.");
            return;
        }

        const confirmed = window.confirm(
            "Möchtest du diesen Archiveintrag endgültig entfernen? " +
            "Danach kann er nicht mehr über das Admin Panel wiederhergestellt werden."
        );
        if (!confirmed) return;

        setBusyId(archiveId);
        setError(null);
        setMessage(null);
        try {
            await deleteDeletedNominationArchiveEntry(token, archiveId);
            await loadDeletedEntries();
            setMessage("Archiveintrag wurde endgültig entfernt.");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Archiveintrag konnte nicht endgültig entfernt werden.");
        } finally {
            setBusyId(null);
        }
    }

    const hasActiveSearch = search.length > 0;
    const isBusy = busyId !== null;

    return (
        <div className="admin-action-block admin-deleted-nominations">
            <h3 className="admin-subtitle">Gelöschte Nominierungen verwalten</h3>
            <p className="admin-note admin-blocklist-description">
                Hier werden Nominierungseinträge angezeigt, die zuvor gelöscht wurden.
                Sie können wiederhergestellt, nachträglich blockiert oder endgültig aus
                dem Archiv entfernt werden.
            </p>
            <p className="admin-note admin-blocklist-warning">
                Einträge, die vor Einführung dieser Archivfunktion gelöscht wurden, können
                hier nicht erscheinen. Beim Wiederherstellen erscheint der Eintrag wieder
                in den Nominierungsständen. Falls der Begriff zusätzlich blockiert ist,
                bleiben neue Nutzer-Nominierungen weiterhin blockiert, bis die Blocklist
                angepasst wird. „Aktuell blockiert?" zeigt, ob der Begriff gerade wirklich
                in der Blocklist steht.
            </p>

            <form className="admin-blocklist-search" onSubmit={handleSearch}>
                <label className="admin-label" htmlFor="deleted-nominations-search">
                    Archiv durchsuchen
                </label>
                <div className="admin-blocklist-search-row">
                    <input
                        id="deleted-nominations-search"
                        className="admin-input"
                        type="search"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        placeholder="Nach Anzeigename oder normalisiertem Wert suchen ..."
                        disabled={loading || isBusy}
                    />
                    <button className="admin-btn-secondary" type="submit" disabled={loading || isBusy}>
                        Suchen
                    </button>
                </div>
            </form>

            {message && <p className="admin-success">{message}</p>}
            {error && <p className="admin-error">{error}</p>}

            {loading ? (
                <p className="admin-note">Gelöschte Nominierungen werden geladen ...</p>
            ) : entries.length === 0 && !error ? (
                <div className="admin-blocklist-empty">
                    <p className="admin-note">
                        {hasActiveSearch
                            ? "Keine gelöschten Einträge für diese Suche gefunden."
                            : "Für diese Kategorie sind aktuell keine gelöschten Nominierungseinträge im Archiv."}
                    </p>
                    {hasActiveSearch && (
                        <button
                            className="admin-btn-secondary"
                            type="button"
                            onClick={resetSearch}
                            disabled={isBusy}
                        >
                            Suche zurücksetzen
                        </button>
                    )}
                </div>
            ) : entries.length > 0 ? (
                <div className="admin-table-scroll">
                    <table className="admin-table admin-deleted-nominations-table">
                        <thead>
                            <tr>
                                <th>Anzeigename</th>
                                <th>Normalisierter Wert</th>
                                <th>Anzahl</th>
                                <th>Löschaktion</th>
                                <th>Aktuell blockiert?</th>
                                <th>Gelöscht am</th>
                                <th>Status</th>
                                <th>Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry) => {
                                const rowBusy = busyId === entry.id;
                                return (
                                    <tr key={entry.id}>
                                        <td>{entry.display_text}</td>
                                        <td><code>{entry.normalized_text}</code></td>
                                        <td>{entry.count}</td>
                                        <td>{deletedActionLabel(entry.deleted_action)}</td>
                                        <td>{entry.currently_blocked ? "Ja" : "Nein"}</td>
                                        <td>{formatDateTime(entry.created_at)}</td>
                                        <td>
                                            {entry.restored
                                                ? `Wiederhergestellt${entry.restored_at ? ` am ${formatDateTime(entry.restored_at)}` : ""}`
                                                : "Im Archiv"}
                                        </td>
                                        <td>
                                            <div className="admin-blocklist-actions">
                                                {entry.restored ? (
                                                    <button
                                                        className="admin-btn-secondary"
                                                        type="button"
                                                        disabled
                                                    >
                                                        Wiederhergestellt
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="admin-btn-primary"
                                                        type="button"
                                                        onClick={() => void handleRestore(entry)}
                                                        disabled={isBusy || loading}
                                                    >
                                                        {rowBusy ? "Wird wiederhergestellt ..." : "Wiederherstellen"}
                                                    </button>
                                                )}
                                                <button
                                                    className="admin-btn-secondary"
                                                    type="button"
                                                    onClick={() => void handleBlock(entry)}
                                                    disabled={isBusy || loading || entry.currently_blocked}
                                                >
                                                    {entry.currently_blocked ? "Bereits blockiert" : "Nachträglich blockieren"}
                                                </button>
                                                <button
                                                    className="admin-btn-secondary admin-btn-muted-danger"
                                                    type="button"
                                                    onClick={() => void handleArchiveDelete(entry)}
                                                    disabled={isBusy || loading}
                                                >
                                                    {rowBusy ? "Wird ausgeführt ..." : "Endgültig entfernen"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </div>
    );
}

export default AdminDeletedNominations;
